import type { InterviewQuestion, InterviewNotesItem } from '@bandos/shared'

export interface ReconciledInterviewQuestion extends InterviewQuestion {
  answers: (string | number | boolean | string[] | null)[]
  isDeleted: boolean
}

/**
 * Merge current questions with all historical interview notes.
 *
 * Matching strategy per snapshot question (in order of preference):
 *   1. Stable ID match — survives reorders and edits, surfaces deletions as ghosts.
 *   2. Positional fallback — used when the snapshot question lacks an `id`
 *      (legacy notes, or notes saved before IDs were assigned).
 */
export function reconcileInterviewQuestions(
  currentQuestions: InterviewQuestion[],
  notes: InterviewNotesItem[]
): ReconciledInterviewQuestion[] {
  const currentBuckets = new Map<string, (string | number | boolean | string[] | null)[]>()
  currentQuestions.forEach((q, i) => {
    currentBuckets.set(q.id ?? `__pos_${i}`, [])
  })

  const deletedBuckets = new Map<
    string,
    { meta: InterviewQuestion; answers: (string | number | boolean | string[] | null)[] }
  >()
  const currentIdSet = new Set(currentQuestions.map((q) => q.id).filter(Boolean))

  for (const note of notes) {
    if (!note.questionsSnapshot || note.questionsSnapshot.length === 0) {
      // Pre-snapshot legacy notes — straight positional match against current questions
      for (let i = 0; i < note.answers.length && i < currentQuestions.length; i++) {
        const key = currentQuestions[i].id ?? `__pos_${i}`
        currentBuckets.get(key)?.push(note.answers[i] ?? null)
      }
      continue
    }

    for (let i = 0; i < note.questionsSnapshot.length; i++) {
      const snap = note.questionsSnapshot[i]
      const answer = note.answers[i] ?? null
      const key = snap.id

      if (key && currentBuckets.has(key)) {
        currentBuckets.get(key)?.push(answer)
      } else if (key && !currentIdSet.has(key)) {
        // ID was active at snapshot time but the question has since been deleted
        if (!deletedBuckets.has(key)) {
          deletedBuckets.set(key, { meta: snap, answers: [] })
        }
        deletedBuckets.get(key)?.answers.push(answer)
      } else if (i < currentQuestions.length) {
        // Snapshot lacks an ID — fall back to positional match against current questions
        const posKey = currentQuestions[i].id ?? `__pos_${i}`
        currentBuckets.get(posKey)?.push(answer)
      }
    }
  }

  const result: ReconciledInterviewQuestion[] = currentQuestions.map((q, i) => ({
    ...q,
    answers: currentBuckets.get(q.id ?? `__pos_${i}`) ?? [],
    isDeleted: false,
  }))

  for (const { meta, answers } of deletedBuckets.values()) {
    if (answers.filter((a) => a !== null).length > 0) {
      result.push({ ...meta, answers, isDeleted: true })
    }
  }

  return result
}
